import { useTranslation } from "react-i18next";
// src/pages/ProductEntry.tsx
import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiPost } from "../api/client";
import { Button } from "../components/Button";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { companyRoute } from "../utils/routeHelpers";
import Inlay from "../components/Inlay";
import { A } from "../styles/appleClasses";

/** ================= Types ================= */
type Product = {
  id: number | string;
  name: string;
  is_service?: boolean;
  sku?: string | null;
  barcode?: string | null;
  sell_price: number; // numeric (2dp)
};

type ProductFormState = {
  name: string;
  is_service: boolean;
  sku: string;
  barcode: string;
  sell_price: string;
};

type CommonProps = {
  variant?: "minimal" | "full";
  companyId?: string | number;
  initial?: Partial<ProductFormState>;
  onCreated?: (product: Product) => void;
  title?: string;
  autoFocusName?: boolean;
};

type PageModeProps = CommonProps & { mode?: "page" };
type InlayModeProps = CommonProps & { mode: "inlay"; onClose: () => void };
type Props = PageModeProps | InlayModeProps;

/** ================= Utils ================= */
function sanitizeMoney(value: string) {
  // allow digits & a single dot; clamp to 2 decimals
  let v = value.replace(/[^0-9.]/g, "");
  const parts = v.split(".");
  if (parts.length > 2) v = parts[0] + "." + parts.slice(1).join("");
  if (parts[1]?.length > 2) v = parts[0] + "." + parts[1].slice(0, 2);
  return v;
}

function to2dpNumber(s: string) {
  const n = parseFloat(s || "0");
  if (isNaN(n) || n < 0) return 0;
  return Math.round(n * 100) / 100;
}

// Prevent iPhone/Safari modal auto-zoom on inlay inputs
const modalInputBase =
  `${A.inputBase} text-[16px] sm:text-[13px] ` +
  `placeholder:text-[16px] sm:placeholder:text-[13px]`;

/** ================= Component ================= */
type DisplayError = { local: "nameRequired" | "createFailed" } | { raw: string } | null;

export default function ProductEntry(props: Props) {
  const { t } = useTranslation();
  const errorText = (error: DisplayError): string | null => error == null ? null : "raw" in error ? error.raw : ({
    nameRequired: t("directory.products.nameRequired"),
    createFailed: t("directory.products.createFailed"),
  })[error.local];

  const {
    variant = "full",
    companyId,
    initial,
    onCreated,
    title = t("directory.products.new"),
    autoFocusName = true,
  } = props;

  const navigate = useNavigate();
  const { company_id: routeCompanyId } = useParams<{ company_id?: string }>();
  const resolvedCompanyId = useMemo(
    () => (companyId ?? routeCompanyId ?? "").toString(),
    [companyId, routeCompanyId],
  );

  const isInlay = props.mode === "inlay";

  const [form, setForm] = useState<ProductFormState>({
    name: initial?.name ?? "",
    sku: initial?.sku ?? "",
    barcode: initial?.barcode ?? "",
    sell_price: initial?.sell_price ?? "",
    is_service: initial?.is_service ?? false,
  });

  const [error, setError] = useState<DisplayError>(null);
  const [saving, setSaving] = useState(false);

  const goList = () => {
    if (resolvedCompanyId)
      navigate(companyRoute(resolvedCompanyId, "/products"));
    else navigate(-1);
  };

  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    `hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD`;

  const hintCls = A.label;
  const inputCls = isInlay ? modalInputBase : A.inputBase;
  const priceInputCls = `${inputCls} text-right ${A.num}`;

  // --- events ---
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "sell_price") {
      setForm((p) => ({ ...p, sell_price: sanitizeMoney(value) }));
    } else {
      setForm((p) => ({ ...p, [name]: value }));
    }
  };

  const handlePriceBlur = () => {
    if (form.sell_price === "") return;
    const num = to2dpNumber(form.sell_price);
    setForm((p) => ({ ...p, sell_price: num.toFixed(2) }));
  };

  async function createProduct(): Promise<Product> {
    const query = resolvedCompanyId
      ? `?company_id=${encodeURIComponent(resolvedCompanyId)}`
      : "";
  const payload = {
    name: form.name.trim(),
    sku: form.sku.trim() || null,
    barcode: form.barcode.trim() || null,
    sell_price: to2dpNumber(form.sell_price),
    is_service: form.is_service,
  };
    return apiPost(`/products${query}`, payload);
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;

    setError(null);

    if (!form.name.trim()) {
      setError({ local: "nameRequired" });
      return;
    }

    setSaving(true);
    try {
      const product = await createProduct();
      onCreated?.(product);

      if (isInlay) {
        props.onClose();
      } else {
        goList();
      }
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail || e?.message;
      setError(msg ? { raw: msg } : { local: "createFailed" });
    } finally {
      setSaving(false);
    }
  };

  /** Reusable form body */
  const renderFormBody = (cancelHandler: () => void) => (
    <form
      onSubmit={handleSubmit}
      className={`space-y-3 ${saving ? "opacity-60 pointer-events-none" : ""}`}
      autoComplete="off"
    >
      {/* Name */}
      <div>
        <input
          type="text"
          name="name"
          value={form.name}
          onChange={handleChange}
          className={inputCls}
          placeholder={t("directory.products.namePlaceholder")}
          required
          autoFocus={autoFocusName && !isInlay}
          aria-label={t("directory.products.nameAria")}
          disabled={saving}
          maxLength={80}
        />
      </div>

      <div>
        <div className="mb-1 text-xs text-gray-500">{t("directory.products.type")}</div>

        <div className="inline-flex w-full rounded-xl border border-gray-200 bg-gray-50 p-1">
          <button
            type="button"
            onClick={() => setForm((p) => ({ ...p, is_service: false }))}
            disabled={saving}
            className={[
              "flex-1 px-3 py-1.5 text-[13px] font-medium rounded-lg transition",
              "focus:outline-none focus:ring-2 focus:ring-appleblueD",
              !form.is_service
                ? "bg-white shadow-sm text-gray-900"
                : "text-gray-500 hover:text-gray-700",
            ].join(" ")}
          >
            {t("directory.products.inventory")}
          </button>

          <button
            type="button"
            onClick={() => setForm((p) => ({ ...p, is_service: true }))}
            disabled={saving}
            className={[
              "flex-1 px-3 py-1.5 text-[13px] font-medium rounded-lg transition",
              "focus:outline-none focus:ring-2 focus:ring-appleblueD",
              form.is_service
                ? "bg-white shadow-sm text-gray-900"
                : "text-gray-500 hover:text-gray-700",
            ].join(" ")}
          >
            {t("directory.products.nonInventory")}
          </button>
        </div>
      </div>

      {variant === "minimal" ? (
        // Minimal: Name + Price (fast create)
        <div>
          <input
            type="text"
            name="sell_price"
            inputMode="decimal"
            value={form.sell_price}
            onChange={handleChange}
            onBlur={handlePriceBlur}
            className={priceInputCls}
            placeholder={t("directory.products.sellPlaceholder")}
            aria-label={t("directory.products.sellPrice")}
            disabled={saving}
          />
          <div className={`mt-1 ${hintCls}`}>
            {t("directory.products.priceHint")}
          </div>
        </div>
      ) : (
        // Full: Name + SKU + Price
        <>
          <div>
            <input
              type="text"
              name="sku"
              value={form.sku}
              onChange={handleChange}
              className={inputCls}
              placeholder={t("directory.products.skuOptional")}
              aria-label={t("directory.products.sku")}
              disabled={saving}
              maxLength={40}
            />
          </div>

          <div>
            <input
              type="text"
              name="barcode"
              value={form.barcode}
              onChange={handleChange}
              className={inputCls}
              placeholder={t("directory.products.barcodeOptional")}
              aria-label={t("directory.products.barcode")}
              disabled={saving}
              maxLength={80}
            />
          </div>

          <div>
            <input
              type="text"
              name="sell_price"
              inputMode="decimal"
              value={form.sell_price}
              onChange={handleChange}
              onBlur={handlePriceBlur}
              className={priceInputCls}
              placeholder={t("directory.products.sellPlaceholder")}
              aria-label={t("directory.products.sellPrice")}
              disabled={saving}
            />
            <div className={`mt-1 ${hintCls}`}>
              {t("directory.products.priceHint")}
            </div>
          </div>

          {/*
            Future "full" fields (uncomment when backend supports):
            <div><AppleComboBox ... /></div>
          */}
        </>
      )}

      {/* Error slot (no layout shift) */}
      <div className="min-h-[18px]">
        {error ? (
          <div className="text-xs text-red-500 font-medium">{errorText(error)}</div>
        ) : null}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2">
        <Button
          type="button"
          variant="secondary"
          onClick={cancelHandler}
          disabled={saving}
        >
          {t("directory.common.cancel")}
        </Button>

        <Button type="submit" variant="primary" disabled={saving}>
          {saving ? t("directory.common.savingDots") : t("directory.common.save")}
        </Button>
      </div>
    </form>
  );

  /** ===== Render per mode ===== */
  if (isInlay) {
    return (
      <Inlay backLabel={t("directory.common.back")} closeLabel={t("directory.common.close")} title={title} onClose={props.onClose} onBack={props.onClose}>
        <div className={`p-4 sm:p-6 ${A.body}`}>
          <div className={`${A.card} p-4 sm:p-6`}>
            {renderFormBody(props.onClose)}
          </div>
        </div>
      </Inlay>
    );
  }

  // Page mode
  return (
    <div className={`min-h-screen bg-gray-50 ${A.body}`}>
      <div className="w-full max-w-xl mx-auto px-2 sm:px-6 pt-4 pb-10">
        {/* Header (match list/detail; spacing tokens: px-2 + mb-2) */}
        <div className="sticky top-0 z-30 bg-gray-50 border-b border-gray-100 px-2 pb-2 mb-2">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className={iconBtn}
              onClick={() => navigate(-1)}
              aria-label={t("directory.common.back")}
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
            </button>

            <h2 className={`flex-1 text-center ${A.title}`}>{title}</h2>

            <button
              type="button"
              className={iconBtn}
              onClick={goList}
              aria-label={t("directory.common.close")}
            >
              <XMarkIcon className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className={`${A.card} p-4 sm:p-6`}>
          {renderFormBody(() => navigate(-1))}
        </div>
      </div>
    </div>
  );
}
